import React, { useState } from 'react';
import './RangeCalendar.css';

interface RangeCalendarProps {
    startDate: string; // ISO format (YYYY-MM-DD)
    endDate: string;   // ISO format (YYYY-MM-DD)
    onChange: (start: string, end: string) => void;
}

const RangeCalendar: React.FC<RangeCalendarProps> = ({ startDate, endDate, onChange }) => {
    const [viewDate, setViewDate] = useState(new Date());

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const prevMonth = () => {
        setViewDate(new Date(year, month - 1, 1));
    };

    const nextMonth = () => {
        setViewDate(new Date(year, month + 1, 1));
    };

    const handleDateClick = (day: number) => {
        const selected = new Date(year, month, day);
        const iso = selected.toISOString().split('T')[0];

        if (!startDate || (startDate && endDate)) {
            // Start fresh
            onChange(iso, '');
        } else {
            // Setting the end
            if (iso < startDate) {
                // If user selects a date before start, make it the new start
                onChange(iso, '');
            } else if (iso === startDate) {
                // If clicking start date again, do nothing or reset?
                // Behavior: reset
                onChange('', '');
            } else {
                onChange(startDate, iso);
            }
        }
    };

    const isSelected = (day: number) => {
        const d = new Date(year, month, day).toISOString().split('T')[0];
        return d === startDate || d === endDate;
    };

    const isInRange = (day: number) => {
        if (!startDate || !endDate) return false;
        const d = new Date(year, month, day).toISOString().split('T')[0];
        return d > startDate && d < endDate;
    };

    const isToday = (day: number) => {
        const today = new Date();
        return (
            day === today.getDate() &&
            month === today.getMonth() &&
            year === today.getFullYear()
        );
    };

    const days = [];
    const totalDays = daysInMonth(year, month);
    const startDay = firstDayOfMonth(year, month);

    // Empty slots for previous month
    for (let i = 0; i < startDay; i++) {
        days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let d = 1; d <= totalDays; d++) {
        const selected = isSelected(d);
        const inRange = isInRange(d);
        const start = startDate === new Date(year, month, d).toISOString().split('T')[0];
        const end = endDate === new Date(year, month, d).toISOString().split('T')[0];

        days.push(
            <div 
                key={d} 
                className={`calendar-day-wrapper ${inRange ? 'in-range' : ''} ${start ? 'range-start' : ''} ${end ? 'range-end' : ''}`}
            >
                <button
                    type="button"
                    className={`calendar-day ${selected ? 'selected' : ''} ${isToday(d) ? 'today' : ''}`}
                    onClick={() => handleDateClick(d)}
                >
                    {d}
                </button>
            </div>
        );
    }

    return (
        <div className="range-calendar">
            <div className="calendar-header">
                <button type="button" onClick={prevMonth} className="nav-btn">‹</button>
                <span className="month-year">{monthNames[month]} {year}</span>
                <button type="button" onClick={nextMonth} className="nav-btn">›</button>
            </div>
            <div className="calendar-weekdays">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="weekday">{d}</div>
                ))}
            </div>
            <div className="calendar-grid">
                {days}
            </div>
        </div>
    );
};

export default RangeCalendar;
